<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref} from 'vue'
import Modal from '@/components/Modal.vue'
import LogService, {LogIslem, LogSayfa} from '@/services/log.ts'
import {useI18n} from 'vue-i18n'
import {useBaglantiHizi} from '@/composables/useBaglantiHizi'
import YarismaService from '@/services/yarisma'

const { t } = useI18n()
const { currentStatus, connectionSpeed, startMonitoring, stopMonitoring } = useBaglantiHizi()

onMounted(() => {
  const baslamasinaKalanSure = YarismaService.getBaslamasinaKalanSure()
  if (baslamasinaKalanSure && baslamasinaKalanSure > 0 && baslamasinaKalanSure < 60 * 40) {
    startMonitoring()
  }
  const bitmesineKalanSure = YarismaService.getBitmesineKalanSure()
  if (baslamasinaKalanSure && baslamasinaKalanSure <= 0 && bitmesineKalanSure && bitmesineKalanSure > 0) {
    startMonitoring()
  }
})

onUnmounted(() => {
  stopMonitoring()
})

const modalRef = ref<InstanceType<typeof Modal>>()

const statuses = computed(() => [
  {
    type: 'offline',
    icon: 'wifi_off',
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    titleMessage: t('connection_info.status_offline_title'),
    titleColor: 'text-red-700',
    textMessage: t('connection_info.status_offline_message'),
  },
  {
    type: 'slow',
    icon: 'android_wifi_4_bar_alert',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50',
    titleMessage: t('connection_info.status_poor_title'),
    titleColor: 'text-yellow-700',
    textMessage: t('connection_info.status_poor_message'),
  },
  {
    type: 'good',
    icon: 'android_wifi_4_bar',
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    titleMessage: t('connection_info.status_good_title'),
    titleColor: 'text-green-700',
    textMessage: t('connection_info.status_good_message'),
  },
])

const currentStatusInfo = computed(() => {
  if (currentStatus.value === 'OFFLINE') {
    return {
      status: 'OFFLINE',
      icon: 'wifi_off',
      color: 'text-red-500',
      bgColor: 'bg-red-50',
    }
  }

  if (currentStatus.value === 'POOR') {
    LogService.log(LogSayfa.ANASAYFA, LogIslem.ANASAYFA_INTERNET_YAVAS, {
      delta: connectionSpeed.value,
    })
    return {
      status: 'POOR',
      icon: 'android_wifi_4_bar_alert',
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-50',
    }
  }

  return {
    status: 'GOOD',
    icon: 'android_wifi_4_bar',
    color: 'text-green-500',
    bgColor: 'bg-green-50',
  }
})
</script>

<template>
  <div class="connection-monitor">
    <button
      class="w-9 h-7 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300"
      :class="currentStatusInfo.bgColor"
      :aria-label="$t('connection_info.title')"
      type="button"
      @click="modalRef?.openModal()"
    >
      <MIcon :name="currentStatusInfo.icon" size="16px" :class="currentStatusInfo.color" />
    </button>

    <Modal ref="modalRef">
      <div
        class="card p-4 flex flex-col items-center max-w-sm mx-auto rounded-4xl gap-4 max-h-[90vh] max-h-[90dvh] md:max-h-none min-h-0 overflow-hidden"
      >
        <div
          class="flex flex-col items-center justify-center w-full my-2 md:my-4 px-2 md:px-4 gap-2 shrink-0"
        >
          <span class="p-2 md:p-3 bg-gray-100 rounded-full flex items-center justify-center">
            <MIcon name="android_wifi_4_bar" size="24px" class="text-gray-700" />
          </span>

          <div class="flex flex-col items-center justify-center w-full px-4">
            <h3 class="text-title-md text-blue-700 text-center">
              {{ $t('connection_info.title') }}
            </h3>
            <p class="text-body-md text-gray-700 mt-2 text-center">
              {{ $t('connection_info.description') }}
            </p>
          </div>
        </div>

        <div class="flex-1 min-h-0 w-full p-4 overflow-y-auto flex flex-col gap-4">
          <div
            class="flex flex-col items-start justify-center w-full gap-2"
            v-for="status in statuses"
            :key="status.type"
          >
            <div
              class="flex items-center justify-center rounded-xl px-2 py-2 gap-2"
              :class="status.bgColor"
            >
              <MIcon :name="status.icon" size="16px" :class="status.color" />
              <p class="text-body-md" :class="status.titleColor">
                {{ status.titleMessage }}
              </p>
            </div>
            <p class="text-body-md text-gray-700">{{ status.textMessage }}</p>
          </div>
        </div>
        <div class="w-full p-4 shrink-0">
          <button class="btn btn-status-default w-full" @click="modalRef?.closeModal()">
            {{ $t('connection_info.ok_button') }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>
